import AlertDismissible from '../../Shared/Components/AlertDismissible';
import Breadcrumbs from '../../Shared/Components/Breadcrumbs';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Layout from '../../Shared/Layout/LayoutAdmin';
import Modal from 'react-bootstrap/Modal';
import React, { useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import { Head, usePage } from '@inertiajs/inertia-react';
import { Inertia } from '@inertiajs/inertia';
import { formatMoney } from '../../utils/formatters';

const Index = ({ metrics, advisers, isAdmin, filters }) => {
    const { flash } = usePage().props;

    const [adviserId, setAdviserId] = useState(filters.adviser || '');
    const [status, setStatus] = useState(filters.status || '');
    const [dateRange, setDateRange] = useState({
        startDate: filters.startDate || '',
        endDate: filters.endDate || '',
    });
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('success');
    const [showLoadingModal, setShowLoadingModal] = useState(false);

    const statusOptions = [
        { value: '', label: 'Todos' },
        { value: 'pending', label: 'Pendiente' },
        { value: 'paid', label: 'Pagado' },
        { value: 'cancelled', label: 'Cancelado' },
    ];

    useEffect(() => {
        if (flash?.success) {
            setAlertMessage(flash.success);
            setAlertType('success');
            setShowAlert(true);
        }
        if (flash?.error) {
            setAlertMessage(flash.error);
            setAlertType('danger');
            setShowAlert(true);
            setShowLoadingModal(false);
        }
    }, [flash]);

    function handleFilter(e) {
        e.preventDefault();

        Inertia.get(route('adviser-commission-reports.index'), {
            adviser: isAdmin ? adviserId || undefined : undefined,
            status: status || undefined,
            startDate: dateRange.startDate || undefined,
            endDate: dateRange.endDate || undefined,
        }, {
            preserveState: true,
            preserveScroll: true,
            replace: true,
        });
    }

    function clearFilters() {
        setAdviserId('');
        setStatus('');
        setDateRange({ startDate: '', endDate: '' });

        Inertia.get(route('adviser-commission-reports.index'), {}, {
            preserveState: true,
            preserveScroll: true,
            replace: true,
        });
    }

    function handleDownloadCfdi(metricId) {
        setShowLoadingModal(true);
        setShowAlert(false);
        window.location.href = route('adviser-commission-reports.download-cfdi', metricId);
        setTimeout(() => setShowLoadingModal(false), 2000);
    }

    function formatPeriod(year, month, day) {
        const months = [
            'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
            'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
        ];
        const safeDay = day || 1;
        return `${safeDay} ${months[month - 1]} ${year}`;
    }

    function statusBadge(metricStatus) {
        if (metricStatus === 'paid') {
            return { className: 'badge badge-success', label: 'Pagado' };
        }
        if (metricStatus === 'cancelled') {
            return { className: 'badge badge-danger', label: 'Cancelado' };
        }
        return { className: 'badge badge-warning', label: 'Pendiente' };
    }

    function adviserName(metric) {
        const a = metric.adviser;
        if (!a) {
            return '—';
        }
        return `${a.user?.name || ''} ${a.lastname_ap || ''} ${a.lastname_am || ''}`.trim();
    }

    function hasSuccessfulInvoice(metric) {
        const invoice = metric.invoice_commission_inbursa;
        return !!(invoice?.success && invoice?.uuid);
    }

    return (
        <>
            <Head title="Consultar Comisiones" />

            <div className="mb-7 mt-7">
                <Breadcrumbs currentPath="/admin/adviser-commission-reports" />
            </div>

            {showAlert && (
                <AlertDismissible
                    message={alertMessage}
                    type={alertType}
                    onClose={() => setShowAlert(false)}
                />
            )}

            <div className="card card-flush mb-5">
                <div className="card-header">
                    <h3 className="card-title">Consultar Comisiones</h3>
                </div>
                <div className="card-body">
                    <p className="text-muted mb-4">
                        Comisiones generadas en Pagos (pendientes, pagadas o canceladas). Solo consulta;
                        la descarga de CFDI aparece cuando hay factura exitosa.
                    </p>

                    <Form onSubmit={handleFilter}>
                        <Row className="g-3 align-items-end">
                            {isAdmin && (
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Asesor</Form.Label>
                                        <Form.Select
                                            value={adviserId}
                                            onChange={(e) => setAdviserId(e.target.value)}
                                        >
                                            <option value="">Todos los asesores</option>
                                            {advisers.map((adviser) => (
                                                <option key={adviser.id} value={adviser.id}>
                                                    {adviser.user?.name} {adviser.lastname_ap} {adviser.lastname_am}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            )}
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>Fecha inicial</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={dateRange.startDate}
                                        onChange={(e) => setDateRange((prev) => ({
                                            ...prev,
                                            startDate: e.target.value,
                                        }))}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>Fecha final</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={dateRange.endDate}
                                        onChange={(e) => setDateRange((prev) => ({
                                            ...prev,
                                            endDate: e.target.value,
                                        }))}
                                        min={dateRange.startDate || undefined}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>Estatus</Form.Label>
                                    <Form.Select
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                    >
                                        {statusOptions.map((option) => (
                                            <option key={option.value || 'all'} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={3} className="d-flex gap-2">
                                <Button type="submit" variant="primary">
                                    Filtrar
                                </Button>
                                <Button type="button" variant="secondary" onClick={clearFilters}>
                                    Limpiar
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </div>
            </div>

            <div className="card card-flush">
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-row-dashed align-middle gs-0 gy-3 my-0">
                            <thead>
                                <tr className="fs-7 fw-bold text-gray-400 border-bottom-0">
                                    {isAdmin && <th className="p-0 pb-3 text-start">ASESOR</th>}
                                    <th className="p-0 pb-3 text-start">PERÍODO</th>
                                    <th className="p-0 pb-3 text-start">TOTAL PRIMA</th>
                                    <th className="p-0 pb-3 text-start">TOTAL BONO</th>
                                    <th className="p-0 pb-3 text-start">PÓLIZAS</th>
                                    <th className="p-0 pb-3 text-start">ESTADO</th>
                                    <th className="p-0 pb-3">CFDI</th>
                                </tr>
                            </thead>
                            <tbody>
                                {metrics.data.length === 0 && (
                                    <tr>
                                        <td colSpan={isAdmin ? 7 : 6} className="text-center text-muted py-8">
                                            No hay comisiones con los filtros seleccionados.
                                        </td>
                                    </tr>
                                )}
                                {metrics.data.map((metric) => {
                                    const badge = statusBadge(metric.status);
                                    const policiesCount = metric.commission_metrics_policies?.length ?? 0;

                                    return (
                                        <tr key={metric.id}>
                                            {isAdmin && (
                                                <td className="fw-bold">{adviserName(metric)}</td>
                                            )}
                                            <td className="fw-bold">
                                                {formatPeriod(metric.year, metric.month, metric.day)}
                                            </td>
                                            <td>{formatMoney(metric.total_premium)}</td>
                                            <td>{formatMoney(metric.total_bonus)}</td>
                                            <td>{policiesCount}</td>
                                            <td>
                                                <span className={badge.className}>{badge.label}</span>
                                            </td>
                                            <td>
                                                {hasSuccessfulInvoice(metric) ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 text-success"
                                                        onClick={() => handleDownloadCfdi(metric.id)}
                                                        disabled={showLoadingModal}
                                                    >
                                                        <i className="bi bi-download me-1"></i>
                                                        Descargar ZIP
                                                    </button>
                                                ) : (
                                                    <span className="text-muted">Sin factura</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {metrics.links && metrics.links.length > 3 && (
                        <div className="d-flex justify-content-center mt-4 gap-1 flex-wrap">
                            {metrics.links.map((link, index) => (
                                <Button
                                    key={index}
                                    size="sm"
                                    variant={link.active ? 'primary' : 'light'}
                                    disabled={!link.url}
                                    onClick={() => link.url && Inertia.get(link.url, {}, { preserveScroll: true })}
                                    dangerouslySetInnerHTML={{ __html: link.label }}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Modal show={showLoadingModal} centered backdrop="static" keyboard={false}>
                <Modal.Body className="text-center py-8">
                    <Spinner animation="border" role="status" className="mb-3" />
                    <p className="mb-0">Descargando factura...</p>
                </Modal.Body>
            </Modal>
        </>
    );
};

Index.layout = (page) => <Layout children={page} title="Consultar Comisiones" />;

export default Index;
